import { GridCellDetail } from "@/grid/components/GridCellDetail";
import { formatMetricForCell, formatMetricValue } from "@/grid/core/format-metric";
import {
  DEFAULT_THRESHOLDS,
  getCellStatusForPeriod,
  resolveActual,
  type StatusColor,
  type StatusThresholds,
} from "@/grid/lib/pacing";
import type { MetricDefinition, PeriodDefinition } from "@/grid/core/types";
import { cn } from "@/lib/cn";
import { useState } from "react";

interface MetricCellProps {
  actual: number | null;
  target?: number | null;
  pacingTarget?: number | null;
  targetSource?: "explicit" | "default" | "inferred" | null;
  metric: MetricDefinition;
  period: PeriodDefinition;
  teamName: string;
  showValues?: boolean;
  thresholds?: StatusThresholds;
  className?: string;
  tvMode?: boolean;
}

const statusClass: Record<StatusColor, string> = {
  green: "bg-[var(--color-status-green)] text-white",
  yellow: "bg-[var(--color-status-yellow)] text-white",
  red: "bg-[var(--color-status-red)] text-white",
};

export function MetricCell({
  actual,
  target = null,
  pacingTarget = null,
  targetSource = null,
  metric,
  period,
  teamName,
  showValues = false,
  thresholds = DEFAULT_THRESHOLDS,
  className,
  tvMode = false,
}: MetricCellProps) {
  const [detailOpen, setDetailOpen] = useState(false);
  const resolvedActual = resolveActual(actual);
  const status = getCellStatusForPeriod(period.id, actual, target, pacingTarget, thresholds);
  const fullActual = formatMetricValue(resolvedActual, metric.format);
  const fullTarget = formatMetricValue(target, metric.format);
  const paceTarget = pacingTarget != null ? formatMetricValue(pacingTarget, metric.format) : null;
  const targetNote =
    targetSource === "default"
      ? " (default weekly target)"
      : targetSource === "inferred"
        ? " (rolled up from stored targets)"
        : "";
  const paceNote =
    paceTarget != null ? ` · Color vs pace to date: ${fullActual} / ${paceTarget}` : "";
  const title =
    target != null
      ? `${teamName} · ${metric.label} · ${period.label}: ${fullActual} / ${fullTarget} goal${targetNote}${paceNote}`
      : paceTarget != null
        ? `${teamName} · ${metric.label} · ${period.label}: ${fullActual} · Color vs pace ${paceTarget}`
        : `${teamName} · ${metric.label} · ${period.label}: ${fullActual}`;

  const displayActual = showValues ? formatMetricForCell(resolvedActual, metric.format) : null;
  const displayTarget =
    showValues && target != null && target > 0 ? formatMetricForCell(target, metric.format) : null;

  return (
    <>
      <button
        type="button"
        title={title}
        aria-label={title}
        onClick={() => setDetailOpen(true)}
        className={cn(
          "rounded-md transition-all duration-300",
          "flex items-center justify-center",
          "hover:ring-2 hover:ring-[var(--color-foreground)]/20 hover:scale-105",
          "focus:outline-none focus:ring-2 focus:ring-[var(--color-foreground)]/30",
          "cursor-pointer active:scale-95",
          statusClass[status],
          className,
        )}
      >
        {showValues && displayActual ? (
          <div
            className={cn(
              "flex flex-col items-center justify-center px-0.5 font-bold leading-tight drop-shadow-md",
              tvMode ? "text-sm" : "text-[10px]",
            )}
          >
            <span>{displayActual}</span>
            {displayTarget ? (
              <span
                className={cn(
                  "font-semibold",
                  tvMode ? "text-xs" : "text-[9px]",
                  targetSource === "default" || targetSource === "inferred"
                    ? "opacity-60 italic"
                    : "opacity-80",
                )}
              >
                /{displayTarget}
              </span>
            ) : null}
          </div>
        ) : null}
      </button>
      <GridCellDetail
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        teamName={teamName}
        metric={metric}
        period={period}
        actual={actual}
        target={target}
        pacingTarget={pacingTarget}
        thresholds={thresholds}
      />
    </>
  );
}
